import {
  useGetStaffListQuery,
  useGetStaffListWithAssignCounterQuery,
} from "@/services/StaffsApi";
import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/UseToast";
import React, { useEffect, useState } from "react";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { MultiSelect } from "@/components/ui/multiselect/MultiSelectCheckBox";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  useAddCounterMutation,
  useUpdateCounterMutation,
} from "@/services/CounterApi";
import { CounterDetails } from "@/app/types/counter";
import { AssigmentStatus, Status } from "@/app/shared_enums/enums";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { useGetTagsQuery } from "@/services/TagsApi";
import { useGetEventsQuery } from "@/services/EventApi";
import { EventAssignment } from "./EventAssignment";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";

const status = [
  { value: "active", label: "Active", color: "bg-green-500" },
  { value: "inactive", label: "Inactive", color: "bg-blue-500" },
];

export interface CounterFormType {
  id?: number;
  status: Status;
  staff_ids: string[];
  code: string;
  tag_id: number[];
  path: number[][];
  event_id?: number;
}

interface CounterFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: "add" | "edit";
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch?: () => void;
  setData?: React.Dispatch<React.SetStateAction<CounterDetails | undefined>>;
  data: CounterDetails | undefined;
  disableCloseConfirmation?: boolean; // Optional prop to disable confirmation dialog
}

const initialValues = {
  id: undefined,
  status: Status.Active,
  staff_ids: [] as string[],
  code: "",
  tag_id: [] as number[],
  path: [] as number[][],
  event_id: undefined as number | undefined,
};

const CounterForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
  disableCloseConfirmation = false, // Default to false
}: CounterFormProps) => {
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const userOrgId = Number(userInfo?.org_id);
  const [eventIds, setEventIds] = useState<(number | undefined)[]>([undefined]);

  const { data: staffOptions, refetch: staffListRefetch } =
    useGetStaffListQuery({
      query: `sort_key=id&sort_order=asc&assignment=unassigned${action == "edit" ? `&counter_id=${data?.id}` : ""}`,
    });

  const { data: eventsResponse } = useGetEventsQuery({ org_id: userOrgId });

  const form = useForm<CounterFormType>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = form;

  const allEventIds = eventsResponse?.data?.map(e => e.event_id) ?? [];
  const { data: tagsList } = useGetTagsQuery(
    { org_id: userOrgId, event_ids: allEventIds }, 
    { skip: allEventIds.length <= 0 }
  );
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const [createCounter] = useAddCounterMutation();
  const [updateCounter] = useUpdateCounterMutation();

  useEffect(() => {
    if (action === "edit" && data) {
      const {path, ...payload} = data;
      (payload as any).staff_ids = payload.staff_members.map(
        (staff: { id: string }) => staff.id
      );
      if (data.path && data.path.length > 0) {
        (payload as any).path = path.map(e => e.split("/").filter(Boolean).map(Number));
      } else {
        (payload as any).path = [];
      }
      reset(payload);
      const tagIdEventIds = data.tag_id
        .map(tid => 
           tagsList?.data?.find(t => t.tag_id === tid)?.event_id??undefined
        )
        .filter(eid => eid !== undefined);
      setEventIds([...new Set(tagIdEventIds)]);
    } else if (action === "add") {
      reset(initialValues);
      staffListRefetch();
      setEventIds([undefined]);
    }
  }, [action, data, reset]);

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const onSubmit = async (payload: CounterFormType) => {
    try {
      console.log("Counter form payload", payload) 
      const paths = payload.path.map((path) => '/' + path.join('/'));
      const tag_id = payload.path.map((path) => path[path.length - 1]);

      if (action === "add") {
        await createCounter({
          staff_ids: payload.staff_ids,
          code: payload.code,
          tag_id: tag_id,
          status: payload.status,
          path: paths as string[],
        })
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Counter created successfully",
            });
            refetch?.();
            handleClose();
            staffListRefetch();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `Failed to create Counter.${err?.data?.detail || err?.data?.message}`,
            });
          });
      } else if (action === "edit") {
        await updateCounter({
          counterId: payload.id as number,
          staff_ids: payload.staff_ids,
          status: payload.status,
          code: payload.code,
          tag_id: tag_id,
          path: paths as string[],
        })
          .unwrap()
          .then((res) => {
            toast({
              variant: "success",
              title: "Counter updated successfully",
            });
            refetch?.();
            handleClose();
            staffListRefetch();
          })
          .catch((err) => {
            toast({
              variant: "destructive",
              title: `${err?.data?.detail || err?.data?.message}`,
            });
          });
      }
    } catch (error: any) {
      console.error("Error on submitting counter", error)
      const message =
        error?.data?.detail || error?.data?.message || "Something went wrong";
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: message,
      });
    }
  };

  const handleConfirm = () => {
    closeDialog(); // Close the dialog
    reset(); // Reset the form
    setData?.(undefined); // Clear the data
    setOpen(false); // Close the form
  };
  // const handleClose = () => {
  //   openDialog(); // Open the confirmation dialog when Cancel is clicked
  // };
  const handleClose = () => {
    clearErrors();
    reset();
    setData?.(undefined);
    setOpen(false);
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (disableCloseConfirmation) {
            handleClose(); // Directly close without confirmation
          } else {
            openDialog(); // Show confirmation dialog
          }
        } else {
          setOpen(open); // Allow opening the form as usual
        }
      }}
    >
      <SheetContent className="h-screen flex flex-col px-0 gap-0 pt-0 sm:w-[40%] sm:max-w-2xl">
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex-1 min-h-0 flex flex-col"
            noValidate
          >
            <SheetHeader className="px-6 pb-3 pt-4">
              <SheetTitle className="pt-0">
                {action === "add" ? "Create" : "Edit"} Counter
              </SheetTitle>
            </SheetHeader>
            <Separator className="h-[1px] font-thin rounded-full" />
            <SheetDescription className="flex-1 min-h-0 overflow-y-auto custom-scrollbar flex flex-col gap-4 py-4 px-6">
              <div className="grid grid-col-2 gap-4">
                <div className={`${action === "add" ? "hidden" : ""}`}>
                  <FloatingLabelInput
                    id="name"
                    label="Counter Id"
                    disabled
                    className="w-full"
                    {...register("id")}
                    error={errors.id?.message}
                  />
                </div>

                <div className="flex gap-2">
                  <div className="relative w-full">
                    <FloatingLabelInput
                      id="code"
                      label="Counter Code"
                      text="*"
                      maxLength={40}
                      className="w-full"
                      {...register("code", {
                        required: "Required",
                        maxLength: {
                          value: 40,
                          message: "Maximum 40 characters allowed",
                        },
                      })}
                      error={errors.code?.message}
                    />
                  </div>

                  <div className="relative w-full">
                    <Controller
                      name="status"
                      rules={{ required: "Required" }}
                      control={control}
                      render={({
                        field: { onChange, value, onBlur },
                        fieldState: { invalid, error },
                      }) => {
                        const statusLabel = status.filter(
                          (r) => r.value == value,
                        )[0];
                        return (
                          <Select
                            onValueChange={(value) => onChange(value)}
                            defaultValue={value}
                          >
                            <SelectTrigger
                              className="Capitalize w-full"
                              floatingLabel="Status"
                              text="*"
                            >
                              <SelectValue
                                placeholder="Select status"
                                className="text-gray-400"
                              >
                                <span className="flex gap-2 items-center">
                                  <span
                                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                                  ></span>
                                  <span>{statusLabel?.label}</span>
                                </span>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {status.map((item) => (
                                <SelectItem key={item.value} value={item.value}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      }}
                    />
                  </div>
                </div>

                <div className="relative ">
                  <Controller
                    name={"staff_ids" as keyof typeof initialValues}
                    rules={{ required: "Required" }}
                    control={control}
                    render={({
                      field: { onChange, value, onBlur },
                      fieldState: { invalid, error },
                    }) => (
                      <MultiSelect
                        floatingLabel={"Assign staff"}
                        text="*"
                        options={
                          staffOptions as {
                            value: string;
                            label: string;
                          }[]
                        }
                        defaultValue={watch("staff_ids") || []} // Ensure defaultValue is always an array
                        onValueChange={(selectedValues) => {
                          onChange(selectedValues); // Pass selected values to state handler
                        }}
                        placeholder={"Select Staff"}
                        variant="inverted"
                        maxCount={1}
                        className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 "
                      />
                    )}
                  />

                  {errors.staff_ids?.message && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {errors.staff_ids?.message}
                    </span>
                  )}
                </div>
              </div>

              <Separator />
              <div className="space-y-2">
                <h2 className="text-black">Event Assignment</h2>
                {eventIds.map((eventId, index) => (
                  <EventAssignment
                    key={eventId}
                    eventId={eventId}
                    setEventId={(eventId) => 
                      setEventIds(eventIds => eventIds.map((eid, i) => i === index ? eventId : eid))
                    }
                    onDelete={() => {
                      setEventIds(eventIds => eventIds.filter((_, i) => i !== index));
                      if (eventId === undefined) return;
                      const eventTagIds = (tagsList?.data || []).filter(tag => tag.event_id === eventId).map(tag => tag.tag_id);
                      form.setValue("path", form.getValues("path").filter(path => 
                        path.length > 0 && !eventTagIds.includes(Number(path[path.length - 1]))
                      ));
                    }}
                    events={(eventsResponse?.data || []).filter(e => e.event_id === eventId || !eventIds.includes(e.event_id))}
                    tags={tagsList?.data || []}
                    form={form}
                  />
                ))}
                <Button 
                  variant="ghost" 
                  className="hover:bg-transparent p-0 h-auto"
                  disabled={eventIds.some(eid => eid === undefined)}
                  onClick={() => { setEventIds(eventIds => [...eventIds, undefined]) }} 
                >
                  <p className="flex items-center"><Plus width={16} /><span className="pt-[3px]">Add Event</span></p>
                </Button>
              </div>
            </SheetDescription>
            <SheetFooter className="flex sm:justify-center">
              <LoadingButton
                type="submit"
                loading={isSubmitting}
                className="bg-primary text-black font-semibold hover:bg-gray-200 w-[90%]"
              >
                Save
              </LoadingButton>
              <ConfirmationDialog
                isOpen={isDialogOpen} // Use the state to control visibility
                onClose={closeDialog} // Close the dialog without taking any action
                onConfirm={handleConfirm} // Perform the cancel action (close form)
              />
            </SheetFooter>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default CounterForm;
